// Claude: buyer digital asset add/edit modal — mirrors AddUrlModal using buyerAsset Redux + BuyerAPI 01-04-2026
import { Box, Button, Flex, Input, Text, VStack, Select } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { errorHandler } from "../../Utils/handler";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Actions";
import { ASSET_TYPES } from "../../Constants/data";

type Props = {
  handleCloseModal: () => void;
  editAsset?: { _id: string; assetTitle: string; assetUrl: string; assetType?: string } | null;
};

const AddBuyerUrlModal = (props: Props) => {
  const buyerAsset = useSelector((state: any) => state.buyerAsset);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [assetType, setAssetType] = useState<string>("");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchDone, setFetchDone] = useState(false);
  const [fetchError, setFetchError] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [finalUrl, setFinalUrl] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const { success, error } = useToastWrapper();

  const isEditMode = !!props.editAsset;

  useEffect(() => {
    if (isEditMode && props.editAsset) {
      dispatch(actions.buyerAssets.setTitle({ title: props.editAsset.assetTitle }));
      dispatch(actions.buyerAssets.setLink({ link: props.editAsset.assetUrl }));
      setAssetType(props.editAsset.assetType || "");
      setFetchDone(true);
    } else {
      dispatch(actions.buyerAssets.resetForm({}));
      setAssetType("");
      setFetchDone(false);
      setFetchError("");
      setFileType("");
      setFinalUrl("");
    }
  }, []);

  const handleFetch = async () => {
    const url = buyerAsset.link?.trim();
    if (!url) { error("Please enter a URL first."); return; }
    setFetchLoading(true);
    setFetchDone(false);
    setFetchError("");
    setFileType("");
    setFinalUrl("");
    try {
      const result = await BuyerAPI.fetchAssetInfo(url);
      if (result?.error) {
        setFetchError(result.error);
        setFetchDone(false);
      } else {
        setFileType(result?.fileType || "Unknown");
        setFinalUrl(result?.finalUrl || "");
        if (result?.title) {
          dispatch(actions.buyerAssets.setTitle({ title: result.title }));
        }
        setFetchDone(true);
      }
    } catch (err) {
      setFetchError("Could not reach this URL. Please check and try again.");
      setFetchDone(false);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError("");
    try {
      if (isEditMode && props.editAsset) {
        await BuyerAPI.updateBuyerAsset(props.editAsset._id, {
          assetTitle: buyerAsset.title,
          assetUrl: buyerAsset.link,
          assetType: assetType || undefined,
        });
        success("Asset updated", "");
      } else {
        await BuyerAPI.addBuyerAsset({
          assetTitle: buyerAsset.title,
          assetUrl: buyerAsset.link,
          assetType: assetType || undefined,
        });
        success("Asset added", "");
      }
      dispatch(actions.buyerAssets.resetForm({}));
      const res = await BuyerAPI.getMyAssets();
      if (res) dispatch(actions.buyerAssets.setAssets({ links: res.data.data }));
      props.handleCloseModal();
    } catch (err) {
      const msg = errorHandler(err);
      setSubmitError(msg);
      error(msg, "");
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled = !fetchDone || !assetType || !buyerAsset.title?.trim();

  return (
    <Box w={"100%"} h={"100%"} pt={"8px"} pb={"16px"} overflowY={"auto"} maxH={"80vh"}>
      <VStack position={"relative"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
        <Box position={"absolute"} right={0} top={0} pb={3} cursor={"pointer"} zIndex={200}>
          <MdClose size={20} onClick={props.handleCloseModal} />
        </Box>
      </VStack>

      <Box position={"relative"} display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"column"}>
        <Text fontSize={"md"} fontWeight={"regular"} textAlign={"center"} color={"black"} mb={"15px"}>
          {isEditMode ? "Edit asset" : "Add asset"}
        </Text>
      </Box>

      <Box>
        <Text fontSize={14} fontWeight={"normal"} mb={1}>Asset Type</Text>
        <Select
          placeholder="Select asset type"
          mb={4} h={"40px"} fontSize={14} borderColor={"gray.300"}
          value={assetType}
          onChange={(e) => setAssetType(e.target.value)}
        >
          {ASSET_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>

        <Text fontSize={14} fontWeight={"normal"} mb={1}>URL</Text>
        <Flex gap={2} mb={fetchError ? 1 : fetchDone ? 1 : 4} alignItems={"center"}>
          <Input
            type="text" flex={1}
            border={"1px"} outline={"none"} _focus={{ outline: "none", border: "0px" }}
            h={"40px"} px={2} borderColor={"gray.300"}
            value={buyerAsset.link}
            onChange={(e) => {
              dispatch(actions.buyerAssets.setLink({ link: e.target.value }));
              setFetchDone(false);
              setFetchError("");
              setFileType("");
              setFinalUrl("");
            }}
          />
          <Button
            onClick={handleFetch} isLoading={fetchLoading}
            h={"40px"} px={4} bg={"#0E122F"} color={"white"}
            _hover={{ bg: "#2E7D32" }} borderRadius={"4px"} fontSize={"sm"} flexShrink={0}
          >
            Fetch
          </Button>
        </Flex>

        {fetchError && <Text fontSize={11} color={"red.500"} mb={3}>{fetchError}</Text>}

        {fetchDone && !fetchError && (
          <Box mb={3}>
            {fileType && <Text fontSize={11} color={"gray.500"}>File type: {fileType}</Text>}
            {finalUrl && <Text fontSize={11} color={"gray.500"} wordBreak="break-all">Resolved to: {finalUrl}</Text>}
          </Box>
        )}

        {fetchDone && (
          <>
            <Text fontSize={11} color={"gray.500"} mb={1}>
              This title helps sellers understand what this asset is. Make it descriptive.
            </Text>
            {!buyerAsset.title?.trim() && (
              <Text fontSize={11} color={"orange.500"} mb={1}>
                Could not auto-detect title. Please enter it manually.
              </Text>
            )}
            <Text fontSize={14} fontWeight={"normal"} mb={1}>Title</Text>
            <Input
              type="text" mb={4}
              border={"1px"} outline={"none"} _focus={{ outline: "none", border: "0px" }}
              h={"40px"} w={"100%"} px={2} borderColor={"gray.300"}
              value={buyerAsset.title}
              onChange={(e) => dispatch(actions.buyerAssets.setTitle({ title: e.target.value }))}
            />
          </>
        )}
      </Box>

      {submitError && <Text fontSize={11} color={"red.500"} mb={2}>{submitError}</Text>}

      <Flex w={"100%"} justifyContent={"space-between"} mt={2} gap={2}>
        <Button
          onClick={props.handleCloseModal}
          flex={1} size={"md"} variant={"outline"}
          borderColor={"card.borderLight"} _hover={{ background: "secondary.light" }} borderRadius={"2px"}
        >
          <Text fontSize={"sm"}>Cancel</Text>
        </Button>
        <Button
          onClick={handleSubmit} isLoading={loading}
          flex={1} size={"md"}
          bg={"#0E122F"} color={"white"} _hover={{ bg: "#2E7D32" }} borderRadius={"2px"}
          isDisabled={isSaveDisabled}
        >
          <Text fontSize={"sm"}>{isEditMode ? "Update" : "Add"}</Text>
        </Button>
      </Flex>
    </Box>
  );
};

export default AddBuyerUrlModal;
